
import React from 'react';
import GameCard from './GameCard';
import { Game, SportType } from '@/types';
import { AlertCircle, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface GamesListProps {
  games: Game[];
  isLoading: boolean;
  error: Error | null;
  showAllScores?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyHint?: string;
}

const GamesList: React.FC<GamesListProps> = ({
  games,
  isLoading,
  error,
  showAllScores = false,
  emptyTitle = "No Matches Found",
  emptyDescription = "No completed games match your current filter criteria.",
  emptyHint = "Try adjusting your filter settings to see more games."
}) => {
  // Filter out future games based on the current time
  const pastGames = games.filter(game => {
    if (game.utcDatetime) {
      const gameDateTime = new Date(game.utcDatetime);
      return gameDateTime <= new Date();
    }
    return false;
  });

  // Sort games from latest to oldest
  const sortedGames = [...pastGames].sort((a, b) => {
    return new Date(b.utcDatetime).getTime() - new Date(a.utcDatetime).getTime();
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-800 h-40 rounded-lg border border-gray-700"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/30 text-red-300 p-4 rounded-lg flex items-center space-x-2 border border-red-800">
        <AlertCircle className="h-5 w-5" />
        <span>Failed to load games. Please try again later.</span>
      </div>
    );
  }

  if (sortedGames.length === 0) {
    return (
      <div className="bg-blue-900/30 text-blue-300 p-6 rounded-lg text-center border border-blue-800 flex flex-col items-center gap-4">
        <Search className="h-10 w-10 opacity-60" />
        <div>
          <h3 className="text-xl font-semibold mb-2">{emptyTitle}</h3>
          <p className="text-blue-200">{emptyDescription}</p>
          <p className="text-sm mt-2 text-blue-300">{emptyHint}</p>
        </div>
      </div>
    );
  }

  // Game card animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 20
    },
    visible: (i: number) => ({ 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.35,
        ease: "easeOut",
        opacity: { duration: 0.5 },
        y: { type: "spring", stiffness: 300, damping: 24 }
      }
    }),
    exit: { 
      opacity: 0, 
      scale: 0.9,
      y: -10,
      transition: { 
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <AnimatePresence initial={false}>
        {sortedGames.map((game, index) => (
          <motion.div
            key={game.id}
            layout
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            custom={index}
            layoutId={game.id}
            whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
          >
            <GameCard game={game} showScores={showAllScores} cardIndex={index} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default GamesList;
